<template>
  <div>
    <div
      class="flex flex-center q-pa-md"
      padding
    >
      <q-form
        ref="studentInformationForm"
        @submit="onSubmit"
        class="q-gutter-md"
        greedy
      >
        <fieldset class="q-gutter-md">
          <legend class="text-left text-h6">
            Student Information
          </legend>
          <q-separator />
          <div class="q-gutter-md row items-start">
            <q-input
              v-model="applicationDetails.firstName"
              label="First Name *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please enter first name']"
              lazy-rules
            />
            <q-input
              v-model="applicationDetails.lastName"
              label="Last Name *"
              style="width: 300px"
              :rules="[ val => !!val || 'Please enter first name']"
              lazy-rules
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-input
              v-model="applicationDetails.preferredFirstName"
              label="Chosen Name"
              style="width: 300px"
            />
            <q-icon
              class="cursor-pointer q-pt-md"
              color="positive"
              size="sm"
              name="help_outline"
            >
              <q-tooltip
                class="text-body2"
                max-width="377px"
                anchor="top middle"
                self="bottom middle"
                :offset="[10, 10]">
                <div class="q-ma-sm">
                Please note that this will be the Name that DigiPen Housing will
                use to refer to you in our communication, but your legal name will
                be used on any school records, and be used on official documents
                that may require your action and/or signature.
                </div>
                <div class="q-ma-sm">
                We understand that the name that you provide may be subject to
                change. If you need to update your chosen name at any time
                – please contact <a href="mailto:housing@digipen.edu">housing@digipen.edu</a>
                </div>
              </q-tooltip>
            </q-icon>

            <q-select
              v-model="applicationDetails.genderIdentity"
              use-input
              hide-selected
              fill-input
              input-debounce="0"
              :options="genderIdentity"
              @filter="filterFn"
              label="Gender Identity *"
              style="width: 300px"
              :rules="[ val => !!val || 'Please select gender identity']"
              lazy-rules
              @update:model-value="(applicationDetails.genderIdentityOther = null)"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-grey">
                    No results
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
            <q-icon
              class="cursor-pointer q-pt-md"
              color="positive"
              size="sm"
              name="help_outline"
            >
              <q-tooltip
                class="text-body2"
                max-width="377px"
                anchor="top middle"
                self="bottom middle"
                :offset="[10, 10]">
                <div class="q-ma-sm">
                    Please select the Gender Identity you most closely align with. We
                    understand that the option you may select may be subject to change.
                    If you need to change your selection after submitting this application,
                    please contact <a href="mailto:housing@digipen.edu">housing@digipen.edu</a>
                </div>
              </q-tooltip>
            </q-icon>
          </div>
          <div
            v-if="applicationDetails.genderIdentity?.toLowerCase()?.trim() === 'other'"
            class="q-gutter-md row items-start"
          >
            <q-input
            v-model="applicationDetails.genderIdentityOther"
            label="Gender Identity (Other) *"
            style="width: 300px"
            :rules="[ val => !!val || 'Please input gender identity']"
            lazy-rules
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-checkbox
              v-model="applicationDetails.mobilePhoneIsInternational"
              label="Is This Number International?"
              :false-value="null"
              style="width: 300px; margin-right: 40px; margin-top: 28px"
            />
            <q-input
              v-model="applicationDetails.mobilePhone"
              label="Mobile Phone *"
              style="width: 300px; margin-right: 40px"
              :mask="applicationDetails.mobilePhoneIsInternational
              ? false : '(###) ###-####'"
              :rules="[ val => !!val || 'Please enter phone number', checkIsValidPhone]"
              lazy-rules
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-checkbox
              v-model="applicationDetails.permanentPhoneIsInternational"
              label="Is This Number International?"
              :false-value="null"
              style="width: 300px; margin-right: 40px; margin-top: 28px"
            />
            <q-input
              v-model="applicationDetails.permanentPhone"
              label="Permanent Phone"
              style="width: 300px; margin-right: 40px"
              :mask="applicationDetails.permanentPhoneIsInternational
              ? false : '(###) ###-####'"
              :rules="[checkIsValidPhoneOrEmpty]"
              lazy-rules
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-input
              v-model="applicationDetails.permanentEmail"
              type="email"
              label="Permanent Email *"
              style="width: 300px"
              :rules="[val => !!val || 'Please enter permanent email', isValidEmail]"
              lazy-rules
            />
            <q-icon
              class="cursor-pointer q-pt-md"
              color="positive"
              size="sm"
              name="help_outline"
            >
              <q-tooltip
                class="text-body2"
                max-width="377px"
                anchor="top middle"
                self="bottom middle"
                :offset="[10, 10]">
                <div class="q-ma-sm">
                Please note that this will not be used as a primary form of contact,
                and is collected as a secondary means to communicate, if necessary.
                All official communication will be sent to your DigiPen email.
                </div>
              </q-tooltip>
            </q-icon>

            <q-input
              v-model="confirmPermanentEmail"
              type="email"
              label="Confirm Permanent Email *"
              style="width: 300px; margin-right: 40px"
              lazy-rules
              :rules="[ val => confirmPermanentEmail == applicationDetails.permanentEmail
              || 'Confirmation email does not match', isValidEmail]"
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-select
              v-model="applicationDetails.academicYear"
              :options="academicYearList"
              label="Academic Year *"
              style="width: 300px"
              :rules="[checkSelect]"
              lazy-rules
            />
            <q-icon
              class="cursor-pointer q-pt-md"
              color="positive"
              size="sm"
              name="help_outline"
            >
              <q-tooltip
                class="text-body2"
                max-width="377px"
                anchor="top middle"
                self="bottom middle"
                :offset="[10, 10]">
                <div class="q-ma-sm">
                Please select the option that best fits your status as of the start
                of the Housing Term you are applying to, and not your current status.
                </div>
                <div class="q-ma-sm">
                EX: I am a junior in Spring 2023 and am filling out my application for
                Fall 2023 housing. When I start in Fall 2023, I will be a senior.
                I select the senior option.
                </div>
              </q-tooltip>
            </q-icon>

            <q-input
              label="Date of Birth *"
              style="width: 300px; margin-right: 40px"
              v-model="applicationDetails.dateOfBirth"
              :rules="[ val => !!val || 'Please enter or select date of birth', checkIsValidDate]"
              lazy-rules
              mask="##/##/####"
            >
              <template v-slot:append>
                <q-icon
                  name="event"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    ref="qDateProxy"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="applicationDetails.dateOfBirth"
                      mask="MM/DD/YYYY"
                      :rules="[ val => !!val || 'Please enter date of birth', checkIsValidDate]"
                      lazy-rules
                    >
                      <div class="row items-center justify-end">
                        <q-btn
                          v-close-popup
                          label="Close"
                          color="primary"
                          flat
                        />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>
          <div
            class="q-gutter-md row items-start"
          >
            <q-select
              v-model="applicationDetails.graduatingThisYearSelector"
              style="width: 656px"
              label="Are you planning on graduating from DigiPen
              Institute of Technology this year? If so, which semester? *"
              :options="[
                { label: 'Fall Semester', value: 'Fall' },
                { label: 'Spring Semester', value: 'Spring' },
                { label: 'Summer Semester', value: 'Summer' },
                { label: 'I do not plan on Graduating this Academic Year', value: 'No' },
              ]"
              @update:model-value="(val) => { applicationDetails.graduatingThisYear = val.value}"
              :rules="[checkSelect]"
              lazy-rules
            />
            <q-icon
              class="cursor-pointer q-pt-md"
              color="positive"
              size="sm"
              name="help_outline"
            >
              <q-tooltip
                class="text-body2"
                max-width="377px"
                anchor="top middle"
                self="bottom middle"
                :offset="[10, 10]">
                  <div class="q-ma-sm">
                      Please select which semester of the upcoming year (if any) that with your
                      current course projections – you are currently
                      planning to apply for graduation in.
                  </div>
              </q-tooltip>
            </q-icon>
          </div>
        </fieldset>
        <br />
        <br />
        <fieldset class="q-gutter-md">
          <legend class="text-left text-h6">
            Address Information
          </legend>
          <q-separator />

          <q-input
            v-model="applicationDetails.permanentAddress"
            label="Permanent Address Line 1 *"
            class="q-gutter-md"
            style="margin-right: 40px"
            :rules="[ val => !!val || 'Please enter permanent address']"
            lazy-rules
          />
          <q-input
            v-model="applicationDetails.permanentAddress2"
            label="Permanent Address Line 2"
            style="margin-right: 40px"
            class="q-gutter-md"
          />
          <div class="q-gutter-md row items-start">
            <q-input
              v-model="applicationDetails.city"
              label="City *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please enter city']"
              lazy-rules
            />
            <q-input
              v-model="applicationDetails.zip"
              label="Postal/Zip Code *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please enter zip code']"
              lazy-rules
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-input
              v-model="applicationDetails.state"
              label="State/Province *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please enter state of residence']"
              lazy-rules
            />
            <!-- <q-input
              v-model="applicationDetails.country"
              label="Country/Region *"
              style="width: 300px"
              :rules="[ val => !!val || 'Please enter country of residence']"
              lazy-rules
            /> -->
            <q-select
              label="Country/Region *"
              v-model="applicationDetails.country"
              use-input
              hide-selected
              fill-input
              input-debounce="0"
              :options="countriesOptions"
              @filter="countriesFilter"
              :rules="[ val => !!val || 'Please enter country of residence']"
              lazy-rules
              style="width: 300px; margin-right: 40px"
            >
              <template v-slot:no-option>
                <q-item>
                  <q-item-section class="text-grey">
                    No results
                  </q-item-section>
                </q-item>
              </template>
            </q-select>
          </div>
          <br />
          <br />
        </fieldset>

        <fieldset class="q-gutter-md">
          <legend class="text-left text-h6">
            Emergency Contact Information
          </legend>
          <q-separator />
          <div
            class="text-left text-body1 q-gutter-md"
            style="max-width: 650px; margin-right: 40px"
          >
            <div>
            Please note that your emergency contacts are not privy to your
            housing records, unless they are granted access by FERPA Proxy,
            which can be done through this portal (Student Landing > Proxies).
            See our <a href="https://www.digipen.edu/student-portal/for-incoming-students/registration/what-is-ferpa" target="_blank">FERPA page</a>
            for more information on your rights as a student.
            </div>
            <div>
            Emergency Contacts may be used in the event that the student has
            experienced severe bodily harm, been transported to a medical facility,
            cannot be located, or another emergency situation in which DigiPen Housing
            staff needs to contact a trusted person of the student.
            </div>
            <div>
            Questions? Contact
            <a href="mailto:housing@digipen.edu">housing@digipen.edu</a>
            </div>
          </div>
          <div class="q-gutter-md row items-start">
            <q-input
              v-model="applicationDetails.emergencyContactName"
              label="Emergency Contact Name *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please enter contact name']"
              lazy-rules
            />
            <q-select
              v-model="applicationDetails.emergencyContactRelationDropdown"
              @update:model-value="(val) => updateContactRelation(val)"
              :options="['Mother', 'Father', 'Legal Guardian',
              'Significant Other', 'Spouse', 'Sibling', 'Other (Please Specify)']"
              label="Contact Relation"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please select contact relation']"
              lazy-rules
            />
          </div>
          <div
            v-if="applicationDetails.emergencyContactRelationDropdown
            === 'Other (Please Specify)'"
            class="q-gutter-md row items-start"
          >
            <q-input
              v-model="applicationDetails.emergencyContactRelation"
              label="Other Contact Relation *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please specify relation']"
              lazy-rules
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-checkbox
              v-model="applicationDetails.emergencyContactIsInternational"
              label="Is This Number International?"
              :false-value="null"
              style="width: 300px; margin-right: 40px; margin-top: 28px"
            />
            <q-input
              v-model="applicationDetails.emergencyContactPhone"
              label="Emergency Contact Phone *"
              style="width: 300px; margin-right: 40px"
              :mask="applicationDetails.emergencyContactIsInternational
              ? false : '(###) ###-####'"
              :rules="[ val => !!val || 'Please enter contacts phone', checkIsValidPhone]"
              lazy-rules
            />
          </div>
          <div class="q-gutter-md row items-start">
            <q-input
              v-model="applicationDetails.emergencyContactEmail"
              type="email"
              label="Emergency contact email *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => !!val || 'Please enter contacts email', isValidEmail]"
              lazy-rules
            />
            <q-input
              v-model="confirmContactEmail"
              type="email"
              label="Confirm emergency contact email *"
              style="width: 300px; margin-right: 40px"
              :rules="[ val => confirmContactEmail == applicationDetails.emergencyContactEmail
              || 'Confirmation email does not match', isValidEmail]"
              lazy-rules
            />
          </div>
        </fieldset>
      </q-form>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import useSession from '@/hooks/session';
import useApplication from '@/hooks/application';
import useUtility from '@/hooks/utility';
import useUiUtilities from '@/utilities/uiUtilities';
import useUiValidations from '@/utilities/uiValidations';

export default {
  methods: {
    async onSubmit() {
      const success = await this.$refs.studentInformationForm.validate();
      if (success) {
        this.$emit('CheckIfFormIsValid', true);
      } else {
        this.$emit('CheckIfFormIsValid', false);
      }
    },
    isValidEmail(val) {
      const emailPattern = /^(?=[a-zA-Z0-9@._%+-]{6,254}$)[a-zA-Z0-9._%+-]{1,64}@(?:[a-zA-Z0-9-]{1,63}\.){1,8}[a-zA-Z]{2,63}$/;
      return emailPattern.test(val) || 'Invalid email';
    },
  },

  async setup() {
    const { sessionDetails } = useSession();
    const { applicationDetails } = useApplication();
    const { academicYearList, getAcademicYears } = useUtility();
    const { countries } = useUiUtilities();
    const {
      checkIsValidDate,
      checkIsValidPhone,
      checkIsValidPhoneOrEmpty,
      checkSelect,
    } = useUiValidations();

    const genderIdentity = ref([]);
    const genderList = [];

    await getAcademicYears();

    for (let i = 0; i < sessionDetails.value.genderOptions.length; i += 1) {
      genderIdentity.value.push(sessionDetails.value.genderOptions[i].sessionOption);
      genderList.push(sessionDetails.value.genderOptions[i].sessionOption);
    }

    // prefilled application gives colleague gender, convert to session gender
    if (applicationDetails.value.genderIdentity) {
      const genderOption = sessionDetails.value.genderOptions
        .find((x) => x.colleagueOption === applicationDetails.value.genderIdentity
          || x.sessionOption === applicationDetails.value.genderIdentity);
      if (genderOption === undefined) {
        applicationDetails.value.genderIdentity = null;
      } else {
        applicationDetails.value.genderIdentity = genderOption.sessionOption;
      }
    }

    const countriesOptions = ref(countries);

    const updateContactRelation = (val) => {
      applicationDetails.value.emergencyContactRelation = val;
      if (val === 'Other (Please Specify)') {
        applicationDetails.value.emergencyContactRelation = '';
      }
    };

    return {
      academicYearList,
      applicationDetails,
      checkIsValidDate,
      checkIsValidPhone,
      checkIsValidPhoneOrEmpty,
      checkSelect,
      confirmPermanentEmail: ref(applicationDetails.value.permanentEmail),
      confirmContactEmail: ref(applicationDetails.value.emergencyContactEmail),
      genderIdentity,
      countriesOptions,
      updateContactRelation,

      filterFn(val, update) {
        update(() => {
          const needle = val.toLowerCase();
          genderIdentity.value = genderList.filter(
            (v) => v.toLowerCase().indexOf(needle) > -1,
          );
        });
      },

      countriesFilter(val, update) {
        update(() => {
          const needle = val.toLowerCase();
          countriesOptions.value = countries.filter((v) => v.toLowerCase().indexOf(needle) > -1);
        });
      },
    };
  },
};
</script>
